<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-overlay" @click.self="handleOverlayClick">
      <div class="modal-container field-property-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="field-info">
            <span class="field-icon" :class="fieldIconClass">
              <i :class="fieldIcon"></i>
            </span>
            <h4>{{ annotation ? (annotation.label || annotation.fieldName) : '' }}</h4>
          </div>
          <button class="close-btn" @click.stop="$emit('close')" title="Close (Esc)">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Tabs -->
        <div class="modal-tabs">
          <button
            v-for="tab in availableTabs"
            :key="tab.key"
            class="tab-btn"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Modal Body with Tab Content -->
        <div class="modal-body">
          <!-- Basic Properties Tab -->
          <div v-show="activeTab === 'basic'" class="tab-panel">
            <div class="property-row">
              <label class="property-label">Label</label>
              <input
                v-model="localAnnotation.label"
                @blur="updateAnnotation"
                class="property-input"
                placeholder="Field label"
              />
            </div>

            <div class="property-row">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="localAnnotation.required"
                  @change="updateAnnotation"
                />
                <span>Required field</span>
              </label>
            </div>

            <!-- Assignable To (for non-signature fields) -->
            <div v-if="annotation.type !== 'signature'" class="property-row">
              <label class="property-label">Assignable To</label>
              <select
                v-model="assignableToValue"
                @change="updateAnnotation"
                class="property-select"
              >
                <option value="">-- Select Role --</option>
                <option value="student">Student</option>
                <option value="guardian">Guardian</option>
                <option value="instructor">Instructor</option>
                <option value="staff">Staff</option>
              </select>
            </div>

            <!-- Signee Type (for signature fields) -->
            <div v-if="annotation.type === 'signature'" class="property-row">
              <label class="property-label">Signee Type</label>
              <select
                v-model="localAnnotation.signee_type"
                @change="updateAnnotation"
                class="property-select"
              >
                <option value="">-- Select Signee --</option>
                <option value="student">Student</option>
                <option value="guardian">Guardian</option>
                <option value="instructor">Instructor</option>
                <option value="staff">Staff</option>
              </select>
            </div>
          </div>

          <!-- Field-Specific Properties Tab -->
          <div v-show="activeTab === 'properties'" class="tab-panel">
            <!-- Auto-fill source (non-signature fields) -->
            <div v-if="autoFillOptions.length" class="property-row">
              <label class="property-label">Auto-fill from</label>
              <select
                v-model="localAnnotation.properties.auto_fill_source"
                @change="updateAnnotation"
                class="property-select"
              >
                <option :value="undefined">-- None (signer fills it in) --</option>
                <optgroup
                  v-for="group in autoFillOptions"
                  :key="group.label"
                  :label="group.label"
                >
                  <option
                    v-for="option in group.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </optgroup>
              </select>
              <p v-if="localAnnotation.properties.auto_fill_source" class="property-hint">
                <i class="fa fa-lock"></i>
                Filled automatically and locked during signing.
              </p>
            </div>

            <!-- Text Field Properties -->
            <template v-if="annotation.type === 'text'">
              <div class="property-row">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    v-model="localAnnotation.properties.multiline"
                    @change="updateAnnotation"
                  />
                  <span>Multiline text</span>
                </label>
              </div>
              <div class="property-row">
                <label class="property-label">Placeholder</label>
                <input
                  v-model="localAnnotation.properties.placeholder"
                  @blur="updateAnnotation"
                  class="property-input"
                  placeholder="Placeholder text"
                />
              </div>
              <div class="property-row">
                <label class="property-label">Max Length</label>
                <input
                  type="number"
                  v-model.number="localAnnotation.properties.maxLength"
                  @blur="updateAnnotation"
                  class="property-input small"
                  min="1"
                  placeholder="Unlimited"
                />
              </div>
            </template>

            <!-- Select Field Properties -->
            <template v-else-if="annotation.type === 'select'">
              <div class="property-row">
                <label class="property-label">Options</label>
                <div class="options-editor">
                  <div
                    v-for="(option, index) in localAnnotation.properties.options"
                    :key="index"
                    class="option-item"
                  >
                    <div class="option-row">
                      <input
                        v-model="option.label"
                        @input="onOptionLabelInput(option)"
                        @blur="updateAnnotation"
                        class="option-input"
                        placeholder="Label"
                      />
                      <button
                        class="btn-toolbar small"
                        @click="removeOption(index)"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                    <div v-if="option.value" class="option-value-preview">
                      value: {{ option.value }}
                    </div>
                  </div>
                  <button
                    class="btn-toolbar add-option"
                    @click="addOption"
                  >
                    <i class="fa fa-plus"></i> Add Option
                  </button>
                </div>
              </div>
            </template>

            <!-- Date Field Properties -->
            <template v-else-if="annotation.type === 'date'">
              <div class="property-row">
                <label class="property-label">Date Format</label>
                <select
                  v-model="localAnnotation.properties.format"
                  @change="updateAnnotation"
                  class="property-select"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div class="property-row">
                <label class="property-label">Min Date</label>
                <input
                  type="date"
                  v-model="localAnnotation.properties.minDate"
                  @change="updateAnnotation"
                  class="property-input"
                />
              </div>
              <div class="property-row">
                <label class="property-label">Max Date</label>
                <input
                  type="date"
                  v-model="localAnnotation.properties.maxDate"
                  @change="updateAnnotation"
                  class="property-input"
                />
              </div>
            </template>

            <!-- Signature Field Properties -->
            <template v-else-if="annotation.type === 'signature'">
              <div class="property-row">
                <label class="property-label">Box Style</label>
                <select
                  v-model="localAnnotation.properties.boxStyle"
                  @change="updateAnnotation"
                  class="property-select"
                >
                  <option value="border">Border</option>
                  <option value="underline">Underline</option>
                  <option value="none">None</option>
                </select>
              </div>
            </template>
          </div>

          <!-- Position Properties Tab -->
          <div v-show="activeTab === 'position'" class="tab-panel">
            <div class="position-grid">
              <div class="position-group">
                <label>Width</label>
                <input
                  type="number"
                  v-model.number="localAnnotation.position.width"
                  @blur="updateAnnotation"
                  class="position-input"
                  min="15"
                />
              </div>
              <div class="position-group">
                <label>Height</label>
                <input
                  type="number"
                  v-model.number="localAnnotation.position.height"
                  @blur="updateAnnotation"
                  class="position-input"
                  min="15"
                />
              </div>
            </div>

            <button
              type="button"
              class="advanced-toggle"
              @click="showAdvanced = !showAdvanced"
            >
              {{ showAdvanced ? 'Hide advanced' : 'Show advanced' }}
            </button>

            <div v-if="showAdvanced" class="position-grid">
              <div class="position-group">
                <label>X</label>
                <input
                  type="number"
                  v-model.number="localAnnotation.position.x"
                  @blur="updateAnnotation"
                  class="position-input"
                />
              </div>
              <div class="position-group">
                <label>Y</label>
                <input
                  type="number"
                  v-model.number="localAnnotation.position.y"
                  @blur="updateAnnotation"
                  class="position-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { slugify } from '../utils/slugify';

export default {
  name: 'FieldPropertyModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    annotation: {
      type: Object,
      default: null
    },
    allAnnotations: {
      type: Array,
      default: () => []
    },
    initialTab: {
      type: String,
      default: 'basic'
    },
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit', 'preview'].includes(value)
    },
    templateId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      localAnnotation: null,
      activeTab: 'basic',
      showAdvanced: false
    };
  },
  computed: {
    fieldIcon() {
      const iconMap = {
        signature: 'fa fa-signature',
        text: 'fa fa-font',
        select: 'fa fa-list',
        date: 'fa fa-calendar'
      };
      return iconMap[this.annotation?.type] || 'fa fa-square';
    },
    fieldIconClass() {
      const classMap = {
        signature: 'icon-signature',
        text: 'icon-text',
        select: 'icon-select',
        date: 'icon-date'
      };
      return classMap[this.annotation?.type] || '';
    },
    availableTabs() {
      const tabs = [
        { key: 'basic', label: 'Basic' }
      ];

      // Add properties tab if field has specific properties
      if (this.annotation?.type) {
        tabs.push({ key: 'properties', label: 'Properties' });
      }

      // Always add position tab
      tabs.push({ key: 'position', label: 'Position' });

      return tabs;
    },
    assignableToValue: {
      get() {
        const list = this.localAnnotation && this.localAnnotation.assignableTo;
        return Array.isArray(list) && list.length ? list[0] : '';
      },
      set(value) {
        if (!this.localAnnotation) return;
        this.localAnnotation.assignableTo = value ? [value] : [];
      }
    },
    // Auto-fill sources offered for the current field type. Values must match
    // the backend AutoFillSource enum. Returns [] for types that can't be
    // auto-filled (e.g. signatures, selects), which hides the dropdown.
    autoFillOptions() {
      const type = this.annotation?.type;

      if (type === 'date') {
        return [
          { label: 'Lesson', options: [{ value: 'lesson_date', label: 'Lesson Date' }] },
          {
            label: 'Student',
            options: [
              { value: 'student_date_of_birth', label: 'Student Date of Birth' },
              { value: 'student_permit_issue_date', label: 'Permit Issue Date' },
              { value: 'student_permit_expiration', label: 'Permit Expiration' },
              { value: 'student_certificate_issue_date', label: 'Certificate Issue Date' }
            ]
          },
          { label: 'Order', options: [{ value: 'order_date', label: 'Order Date' }] },
          { label: 'Date', options: [{ value: 'today_date', label: 'Date Signed' }] }
        ];
      }

      if (type === 'text') {
        return [
          {
            label: 'Lesson',
            options: [
              { value: 'lesson_instructor_name', label: 'Instructor Name' },
              { value: 'lesson_start_time', label: 'Lesson Start Time' },
              { value: 'lesson_pickup_location', label: 'Pickup Location' },
              { value: 'lesson_dropoff_location', label: 'Drop-off Location' },
              { value: 'lesson_vehicle', label: 'Vehicle' }
            ]
          },
          {
            label: 'Student',
            options: [
              { value: 'student_name', label: 'Student Name' },
              { value: 'student_first_name', label: 'First Name' },
              { value: 'student_last_name', label: 'Last Name' },
              { value: 'student_email', label: 'Email' },
              { value: 'student_phone', label: 'Phone' },
              { value: 'student_permit_number', label: 'Permit Number' },
              { value: 'student_certificate_number', label: 'Certificate Number' },
              { value: 'student_address', label: 'Student Address' }
            ]
          },
          {
            label: 'School',
            options: [
              { value: 'school_name', label: 'School Name' },
              { value: 'school_address', label: 'School Address' }
            ]
          },
          {
            label: 'Document',
            options: [
              { value: 'document_number', label: 'Document Number' }
            ]
          },
          {
            label: 'Order',
            options: [
              { value: 'order_number', label: 'Order Number' },
              { value: 'order_total', label: 'Order Total' },
              { value: 'order_status', label: 'Order Status' },
              { value: 'order_payment_method', label: 'Payment Method' },
              { value: 'order_card_last4', label: 'Card Last 4' },
              { value: 'order_line_items', label: 'Line Items' }
            ]
          }
        ];
      }

      return [];
    }
  },
  watch: {
    annotation: {
      handler(newAnnotation) {
        if (newAnnotation) {
          this.localAnnotation = this.cloneAnnotation(newAnnotation);
        } else {
          this.localAnnotation = null;
        }
      },
      immediate: true
    },
    initialTab: {
      handler(newTab) {
        if (newTab) {
          this.activeTab = newTab;
        }
      },
      immediate: true
    },
    visible(newVal) {
      if (newVal) {
        // Add event listeners when modal opens
        this.$nextTick(() => {
          this.addEventListeners();
        });
      } else {
        // Remove event listeners when modal closes
        this.removeEventListeners();
      }
    }
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  methods: {
    cloneAnnotation(annotation) {
      if (!annotation) return null;

      const cloned = {
        ...annotation,
        position: { ...annotation.position },
        properties: this.cloneProperties(annotation.properties),
        assignableTo: [...(annotation.assignableTo || [])],
        signee_type:
          annotation.signee_type ||
          annotation.properties?.signee_type ||
          (annotation.assignableTo && annotation.assignableTo[0]) ||
          ''
      };

      this.syncSignatureSignee(cloned);

      return cloned;
    },

    cloneProperties(properties) {
      if (!properties) return {};

      const cloned = { ...properties };

      // Deep clone options array for select fields
      if (cloned.options && Array.isArray(cloned.options)) {
        cloned.options = cloned.options.map(option => ({ ...option }));
      }

      return cloned;
    },

    updateAnnotation() {
      if (this.localAnnotation) {
        this.syncSignatureSignee(this.localAnnotation);
        this.$emit('update', this.localAnnotation);
      }
    },

    addOption() {
      if (this.localAnnotation && this.localAnnotation.type === 'select') {
        if (!this.localAnnotation.properties.options) {
          this.localAnnotation.properties.options = [];
        }
        this.localAnnotation.properties.options.push({
          label: '',
          value: ''
        });
        this.updateAnnotation();
      }
    },

    onOptionLabelInput(option) {
      option.value = slugify(option.label || '');
    },

    removeOption(index) {
      if (this.localAnnotation && this.localAnnotation.type === 'select') {
        this.localAnnotation.properties.options.splice(index, 1);
        this.updateAnnotation();
      }
    },

    handleOverlayClick(event) {
      // Stop propagation to prevent document click handlers from being triggered
      event.stopPropagation();
      this.$emit('close');
    },

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        // Stop propagation to prevent toolbar's Escape handler from also firing
        event.stopPropagation();
        this.$emit('close');
      }
    },

    addEventListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
    },

    removeEventListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
    },

    syncSignatureSignee(annotation) {
      if (!annotation || annotation.type !== 'signature') {
        return;
      }

      const resolvedSignee =
        annotation.signee_type ||
        annotation.properties?.signee_type ||
        (annotation.assignableTo && annotation.assignableTo[0]) ||
        '';

      annotation.signee_type = resolvedSignee;

      if (resolvedSignee) {
        annotation.assignableTo = [resolvedSignee];

        // signee_type is only at top-level now, not in properties
      } else {
        annotation.assignableTo = [];

        // Clean up any legacy properties.signee_type if present
        if (annotation.properties && Object.prototype.hasOwnProperty.call(annotation.properties, 'signee_type')) {
          const { signee_type, ...rest } = annotation.properties;
          annotation.properties = rest;
        }
      }
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

/* Modal Container */
.field-property-modal {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f7fafc;
  border-radius: 8px 8px 0 0;
}

.field-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.field-info h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  font-size: 16px;
}

.icon-signature {
  background-color: rgba(66, 153, 225, 0.1);
  color: #4299e1;
}

.icon-text {
  background-color: rgba(72, 187, 120, 0.1);
  color: #48bb78;
}

.icon-select {
  background-color: rgba(159, 122, 234, 0.1);
  color: #9f7aea;
}

.icon-date {
  background-color: rgba(237, 137, 54, 0.1);
  color: #ed8936;
}

.close-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s;
  color: #4a5568;
  font-size: 18px;
}

.close-btn:hover {
  background-color: rgba(113, 128, 150, 0.1);
  border-color: rgba(113, 128, 150, 0.3);
  color: #718096;
}

/* Modal Tabs */
.modal-tabs {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f9fafb;
  padding: 0 12px;
}

.tab-btn {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 12px 20px;
  margin-bottom: -1px;
  cursor: pointer;
  color: #718096;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #2d3748;
  background-color: rgba(0, 0, 0, 0.02);
}

.tab-btn.active {
  color: #4299e1;
  border-bottom-color: #4299e1;
  background-color: #fff;
}

/* Modal Body */
.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.tab-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.property-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.property-label {
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
}

.property-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: #718096;
}

.property-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s;
}

.property-input:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.property-input.small {
  width: 120px;
}

.property-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.property-select:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #2d3748;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.role-checkboxes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.role-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}

.role-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

/* Options Editor */
.options-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-value-preview {
  font-size: 11px;
  color: #a0aec0;
  padding-left: 10px;
}

.option-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
}

.btn-toolbar {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #4a5568;
}

.btn-toolbar:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}

.btn-toolbar.small {
  padding: 4px 8px;
  font-size: 12px;
}

.add-option {
  align-self: flex-start;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4299e1;
}

.add-option:hover {
  background-color: rgba(66, 153, 225, 0.1);
  border-color: rgba(66, 153, 225, 0.3);
}

/* Position Grid */
.position-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.position-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.position-group label {
  font-size: 12px;
  font-weight: 500;
  color: #718096;
}

.position-input {
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
}

.advanced-toggle {
  background: none;
  border: none;
  padding: 0;
  margin: 16px 0 12px;
  color: #718096;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
}

.advanced-toggle:hover {
  color: #2b83fc;
  text-decoration: underline;
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .field-property-modal,
.modal-fade-leave-active .field-property-modal {
  transition: transform 0.3s ease;
}

.modal-fade-enter .field-property-modal,
.modal-fade-leave-to .field-property-modal {
  transform: scale(0.9);
}

/* Scrollbar styling */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
